import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { LightboxImageTrigger, LightboxProvider } from "@/modern/components/ui/lightbox";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/modern/components/ui/sheet";
import type { TemporalPhaseTag } from "@/react/components/marketing/temporal";
import type { PromoteSurfaceAsset, SurfaceSolutionTag, SurfaceWriteAction } from "../api/types";
import { getAvailableActions, skipActionLabel } from "../utils/actionMatrix";
import { assetTagIds, getAcceptReadiness } from "../utils/mergeTagIds";
import PromoteSurfaceAcceptFields from "./PromoteSurfaceAcceptFields";

type Props = {
  open: boolean;
  assets: PromoteSurfaceAsset[];
  index: number;
  tags: SurfaceSolutionTag[];
  temporalTags: TemporalPhaseTag[];
  boardUserAvailable?: boolean;
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  onIndexChange: (index: number) => void;
  onAccept: (
    asset: PromoteSurfaceAsset,
    solutionTagIds: number[],
    extras?: {
      caption?: string | null;
      alt_text?: string | null;
      temporalTagId?: number | null;
    }
  ) => void | Promise<void>;
  onAction: (action: Exclude<SurfaceWriteAction, "accept">, asset: PromoteSurfaceAsset) => void | Promise<void>;
};

/**
 * Right-side review sheet — keeps the grid in view while inspecting / actioning one asset.
 * Click the preview to open the shared full-size Lightbox (same as Coverage / Spot Incentive).
 */
export default function PromoteSurfaceReviewOverlay({
  open,
  assets,
  index,
  tags,
  temporalTags,
  boardUserAvailable = true,
  busy,
  onOpenChange,
  onIndexChange,
  onAccept,
  onAction,
}: Props) {
  const asset = assets[index];
  const [solutionTagIds, setSolutionTagIds] = useState<number[]>([]);
  const [temporalTagId, setTemporalTagId] = useState<number | null>(null);
  const [caption, setCaption] = useState("");
  const [altText, setAltText] = useState("");
  const [previewLoaded, setPreviewLoaded] = useState(false);

  useEffect(() => {
    if (!asset) return;
    setCaption(asset.caption ?? "");
    setAltText(asset.alt_text ?? "");
    setSolutionTagIds([]);
    const existingIds = assetTagIds(asset);
    const existingTemporal =
      temporalTags.find((t) => existingIds.includes(t.id))?.id ?? temporalTags[0]?.id ?? null;
    setTemporalTagId(existingTemporal);
  }, [asset?.id, temporalTags]);

  const previewSrc = asset?.url || asset?.thumbnail_url || "";

  useEffect(() => {
    setPreviewLoaded(false);
  }, [previewSrc, asset?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        onIndexChange(index - 1);
      } else if (event.key === "ArrowRight" && index < assets.length - 1) {
        event.preventDefault();
        onIndexChange(index + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, assets.length, onIndexChange]);

  const lightboxImages = useMemo(
    () =>
      assets.map((a) => ({
        src: a.url || a.thumbnail_url || "",
        alt: a.alt_text || a.caption || `Asset ${a.id}`,
        caption: a.caption || undefined,
      })),
    [assets]
  );

  if (!assets.length) return null;

  const actions = getAvailableActions(asset?.verification_status);
  const { needsSolutionTag, needsTemporalTag } = getAcceptReadiness({
    solutionTagIds,
    temporalTagId,
    temporalTagCount: temporalTags.length,
  });
  const requireAcceptFields = Boolean(actions.accept);
  const missingSolution = requireAcceptFields && needsSolutionTag;
  const missingTemporal = requireAcceptFields && needsTemporalTag;
  const missingBoardUser = !boardUserAvailable;
  const writesBlocked = Boolean(busy) || missingBoardUser;
  const acceptDisabled = writesBlocked || missingSolution || missingTemporal;
  const otherDisabled = writesBlocked;
  const src = previewSrc;
  const statusLabel = asset?.verification_status?.replaceAll("_", " ") ?? "Asset";

  const disableHints: string[] = [];
  if (busy) {
    disableHints.push("Action in progress. Wait for it to finish.");
  } else if (missingBoardUser) {
    disableHints.push("A board user is required before you can accept, reject, or skip.");
  } else if (missingTemporal) {
    disableHints.push("Select a temporal phase (Before, During, or After) to enable Accept.");
  } else if (missingSolution) {
    disableHints.push("Select at least one solution tag to enable Accept.");
  }

  return (
    <LightboxProvider images={lightboxImages} allowZoom onIndexChange={onIndexChange}>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
        >
          <SheetHeader className="shrink-0 space-y-1 border-b border-border px-5 py-4 pr-12 text-left">
            <SheetTitle className="truncate text-base">
              {asset?.caption || `Asset ${asset?.id ?? ""}`}
            </SheetTitle>
            <SheetDescription className="flex items-center gap-2 text-xs">
              <span className="rounded-md bg-muted px-1.5 py-0.5 font-medium uppercase tracking-wide text-foreground">
                {statusLabel}
              </span>
              <span className="tabular-nums text-muted-foreground">
                {index + 1} / {assets.length}
              </span>
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* min-h keeps prev/next controls in the preview band while the image loads */}
            <div className="group/preview relative min-h-[12.5rem] bg-muted sm:min-h-[16rem]">
              {src ? (
                <LightboxImageTrigger index={index} className="block w-full cursor-zoom-in">
                  <div className="relative flex min-h-[12.5rem] max-h-[42vh] items-center justify-center sm:min-h-[16rem]">
                    {!previewLoaded && (
                      <div
                        className="absolute inset-0 animate-pulse bg-muted-foreground/10"
                        aria-hidden
                      />
                    )}
                    <img
                      src={src}
                      alt={asset?.alt_text || asset?.caption || `Asset ${asset?.id}`}
                      className={`max-h-[42vh] w-full object-contain transition-opacity duration-200 ${
                        previewLoaded ? "opacity-100" : "opacity-0"
                      }`}
                      onLoad={() => setPreviewLoaded(true)}
                      onError={() => setPreviewLoaded(true)}
                    />
                    <span className="pointer-events-none absolute bottom-3 left-1/2 z-[5] flex -translate-x-1/2 items-center gap-1.5 rounded-full border-2 border-white bg-white px-3 py-1.5 text-xs font-bold text-neutral-900 shadow-lg opacity-0 transition group-hover/preview:opacity-100">
                      <Expand className="h-3.5 w-3.5" aria-hidden />
                      View full size
                    </span>
                  </div>
                </LightboxImageTrigger>
              ) : (
                <div className="flex min-h-[12.5rem] items-center justify-center text-sm text-muted-foreground sm:min-h-[16rem]">
                  No preview
                </div>
              )}

              <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-between px-2">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="pointer-events-auto h-10 w-10 rounded-full border-2 border-white bg-white text-neutral-900 shadow-[0_4px_14px_rgba(0,0,0,0.45)] hover:bg-neutral-100 hover:text-neutral-950 disabled:opacity-40"
                  disabled={index <= 0}
                  onClick={() => onIndexChange(index - 1)}
                  aria-label="Previous asset"
                >
                  <ChevronLeft className="h-5 w-5" strokeWidth={2.5} />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="pointer-events-auto h-10 w-10 rounded-full border-2 border-white bg-white text-neutral-900 shadow-[0_4px_14px_rgba(0,0,0,0.45)] hover:bg-neutral-100 hover:text-neutral-950 disabled:opacity-40"
                  disabled={index >= assets.length - 1}
                  onClick={() => onIndexChange(index + 1)}
                  aria-label="Next asset"
                >
                  <ChevronRight className="h-5 w-5" strokeWidth={2.5} />
                </Button>
              </div>
            </div>

            {asset && (
              <div className="space-y-5 px-5 py-5">
                {actions.accept && (
                  <div className="space-y-5">
                    <PromoteSurfaceAcceptFields
                      tags={tags}
                      temporalTags={temporalTags}
                      solutionTagIds={solutionTagIds}
                      onSolutionTagIdsChange={setSolutionTagIds}
                      temporalTagId={temporalTagId}
                      onTemporalTagIdChange={setTemporalTagId}
                      disabled={Boolean(busy)}
                    />

                    <section className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                          Caption
                        </label>
                        <Input
                          value={caption}
                          onChange={(e) => setCaption(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                          Alt text
                        </label>
                        <Input
                          value={altText}
                          onChange={(e) => setAltText(e.target.value)}
                        />
                      </div>
                    </section>
                  </div>
                )}

                {(asset.ai_observations?.length ?? 0) > 0 && (
                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      AI observations
                    </h3>
                    <ul className="space-y-1.5 rounded-lg border border-border bg-muted/40 p-3 text-sm">
                      {asset.ai_observations!.map((obs) => (
                        <li key={obs} className="flex gap-2">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/40" />
                          <span>{obs}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {asset.actioned_by_name && (
                  <p className="text-xs text-muted-foreground">
                    Last actioned by {asset.actioned_by_name}
                    {asset.actioned_at
                      ? ` · ${new Date(asset.actioned_at).toLocaleString()}`
                      : null}
                  </p>
                )}
              </div>
            )}
          </div>

          {asset && (
            <div className="shrink-0 border-t border-border bg-background px-5 py-4">
              {disableHints.length > 0 && (
                <div
                  className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
                  role="status"
                >
                  {disableHints.map((hint) => (
                    <p key={hint}>{hint}</p>
                  ))}
                </div>
              )}
              <SheetFooter className="flex-row flex-wrap items-center justify-stretch gap-2 p-0 sm:space-x-0">
                {actions.accept && (
                  <Button
                    disabled={acceptDisabled}
                    className="flex-1"
                    title={
                      missingTemporal
                        ? "Select a temporal phase"
                        : missingSolution
                          ? "Select at least one solution tag"
                          : missingBoardUser
                            ? "Board user required"
                            : busy
                              ? "Busy"
                              : undefined
                    }
                    onClick={() =>
                      onAccept(asset, solutionTagIds, {
                        caption: caption || null,
                        alt_text: altText || null,
                        temporalTagId,
                      })
                    }
                  >
                    Accept
                  </Button>
                )}
                {actions.reject && (
                  <Button
                    variant="secondary"
                    disabled={otherDisabled}
                    className="flex-1"
                    title={
                      missingBoardUser ? "Board user required" : busy ? "Busy" : undefined
                    }
                    onClick={() => onAction("reject", asset)}
                  >
                    Reject
                  </Button>
                )}
                {actions.skip && (
                  <Button
                    variant="secondary"
                    disabled={otherDisabled}
                    className="flex-1"
                    title={
                      missingBoardUser ? "Board user required" : busy ? "Busy" : undefined
                    }
                    onClick={() => onAction("skip", asset)}
                  >
                    {skipActionLabel(asset?.verification_status)}
                  </Button>
                )}
                {actions.reprocess && (
                  <Button
                    variant="outline"
                    disabled={Boolean(busy)}
                    className="flex-1"
                    title={busy ? "Busy" : undefined}
                    onClick={() => onAction("reprocess", asset)}
                  >
                    Reprocess
                  </Button>
                )}
              </SheetFooter>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </LightboxProvider>
  );
}
